/**
 * lib/examRegister.ts — who should be in the room, and who was.
 * ------------------------------------------------------------------
 * Pure, like examDuties.ts beside it. Phase 3 of the Exams section: the
 * register an invigilator fills in the hall, replacing the printed sheet the
 * office types up afterwards.
 *
 * THE STATEMENT OF ENTRY DECIDES WHO IS ENTERED, exactly as it does for a
 * pupil's own exam timetable — the subject defaults were wrong for 108 of 113
 * candidates, and the office's rota is a working document, not the entry. So
 * the list comes from `reportExamEntries` and the rota's own count is shown
 * beside it as a CHECK. On the live November 2026 rota the two disagree on 21
 * of 89 papers, including two papers mis-coded on the sheet (a "0620/42" that
 * is really 0620/32, a "3158/02" that is 0698/02) — the sort of thing that is
 * cheap to fix in September and expensive to discover in a hall in October.
 *
 * EVERYONE STARTS PRESENT and the invigilator marks the absentees (the
 * school, 2026-09-18): in a room of thirty that is two taps rather than
 * thirty. The danger in that default is a register nobody has filled looking
 * like a full house, so an unconfirmed register is NOT a record of a full
 * house — `registerState` calls it 'notTaken' and the screen says so. Only
 * CONFIRMING turns it into evidence, and a confirmed register carries the
 * name and the time.
 *
 * A REGISTER IS PER PAPER, not per rota row. Where the office splits a paper
 * across two rooms (9702/22 on 14 October) the candidates are one entry list;
 * rooms arrive with the seating phase, and until then dividing the list would
 * be inventing a fact.
 */

/** One candidate on one paper, from their statement of entry. */
export interface RegisterCandidate {
  studentId: string;
  candidateNumber: string;
  candidateName: string;
  className: string;
}

export type AttendanceStatus = 'present' | 'absent';

/** What the invigilator recorded against a candidate. Absent from the map
 *  means present and unremarkable — the common case, and the reason the map
 *  is sparse rather than a row per candidate. */
export interface RegisterMark {
  status: AttendanceStatus;
  /** 'HH:MM' — a candidate who arrived after the start. */
  arrived?: string;
  /** Illness, an irregularity, left early. Cambridge asks for these. */
  note?: string;
}

export interface ExamRegister {
  id: string;
  seriesId: string;
  code: string;
  date: string;
  session: string;
  subject: string;
  component: string;
  start: string;
  end: string;
  /** The rota's own figure, kept for the cross-check — never the list. */
  officeCount: number;
  /** Who may fill this one in, besides the exam managers. */
  invigilatorIds: string[];
  marks: Record<string, RegisterMark>;
  /** Frozen at confirmation: what was actually counted in the room that day.
   *  Entries are corrected afterwards (they were, 21 times, before the series
   *  even began), and a register that re-derived itself would rewrite
   *  history. Absent before it is confirmed. */
  totals?: { entered: number; present: number; absent: number };
  confirmedAt?: string;
  confirmedBy?: string;
  confirmedByName?: string;
  reopenedAt?: string;
  reopenedByName?: string;
  updatedAt?: string;
  updatedBy?: string;
}

/** `{seriesId}__{date}__{session}__{code}`, matching the duty document of the
 *  same paper so the two are obviously the same thing in a console. */
export function registerId(seriesId: string, date: string, session: string, code: string): string {
  return `${seriesId}__${date}__${session}__${code.replace('/', '-')}`;
}

/* ── who is entered ───────────────────────────────────────────────── */

/** An entry component as `reportExamEntries` stores it. */
interface EntryLike { code: string; date: string; session: string }
interface CandidateLike {
  studentId: string;
  candidateNumber: string;
  candidateName: string;
  className?: string;
  components?: EntryLike[];
}

/**
 * Does this entry belong to this paper?
 *
 * Code and date must agree. SESSION IS TOLERATED WHEN THE STATEMENT HAS NONE:
 * a test-window paper (ICT 0417/02 and /03 — 37 candidates each) carries no
 * session at all, and the school schedules it into one. Requiring a match
 * there produced two papers whose register was empty while 37 pupils sat
 * them.
 */
export function entryMatchesPaper(entry: EntryLike, code: string, date: string, session: string): boolean {
  if (entry.code !== code || entry.date !== date) return false;
  return !entry.session || !session || entry.session === session;
}

/** Everyone entered for a paper, in candidate-number order — which is the
 *  order the desks are numbered and the order a register is read. */
export function candidatesForPaper(
  candidates: CandidateLike[],
  code: string,
  date: string,
  session: string,
): RegisterCandidate[] {
  return candidates
    .filter((c) => (c.components ?? []).some((e) => entryMatchesPaper(e, code, date, session)))
    .map((c) => ({
      studentId: c.studentId,
      candidateNumber: c.candidateNumber,
      candidateName: c.candidateName,
      className: c.className ?? '',
    }))
    .sort((a, b) => a.candidateNumber.localeCompare(b.candidateNumber, undefined, { numeric: true })
      || a.candidateName.localeCompare(b.candidateName));
}

/* ── the state of a register ──────────────────────────────────────── */

export type RegisterState = 'notTaken' | 'taken';

/** Taken means CONFIRMED. An unconfirmed register with no marks is not a
 *  room full of present candidates; it is a room nobody has counted. */
export function registerState(register: Pick<ExamRegister, 'confirmedAt'> | null | undefined): RegisterState {
  return register?.confirmedAt ? 'taken' : 'notTaken';
}

/** A confirmed register is locked to the invigilator; only an exam manager
 *  may reopen it, and that is stamped too. */
export function isLocked(register: Pick<ExamRegister, 'confirmedAt'> | null | undefined): boolean {
  return Boolean(register?.confirmedAt);
}

export function canFillRegister(
  register: Pick<ExamRegister, 'invigilatorIds' | 'confirmedAt'> | null | undefined,
  teacherId: string,
  isManager: boolean,
): boolean {
  if (isManager) return true;
  if (!register || !teacherId) return false;
  if (isLocked(register)) return false;
  return (register.invigilatorIds ?? []).includes(teacherId);
}

/* ── the figures ──────────────────────────────────────────────────── */

export interface RegisterTotals {
  entered: number;
  present: number;
  absent: number;
  late: number;
  noted: number;
  /** One script per candidate who sat it — what goes in the envelope. */
  scripts: number;
}

export function registerTotals(
  candidates: RegisterCandidate[],
  marks: Record<string, RegisterMark>,
): RegisterTotals {
  let absent = 0; let late = 0; let noted = 0;
  for (const c of candidates) {
    const mark = marks[c.studentId];
    if (!mark) continue;
    if (mark.status === 'absent') absent += 1;
    else if (mark.arrived) late += 1;
    if (mark.note) noted += 1;
  }
  const present = candidates.length - absent;
  return { entered: candidates.length, present, absent, late, noted, scripts: present };
}

/**
 * What to say when the rota's count and the statements disagree.
 *
 * Returns null when they agree or when the office recorded nothing, so the
 * screen carries the line only where there is something to fix.
 */
export function entryDiscrepancy(entered: number, officeCount: number): string | null {
  if (!officeCount || officeCount === entered) return null;
  const more = entered > officeCount;
  return `The statements of entry list ${entered} candidate${entered === 1 ? '' : 's'}, `
    + `the office's rota says ${officeCount}. ${more ? 'Someone entered is not on the rota' : 'The rota expects candidates the statements do not show'}`
    + ' — worth settling before the day.';
}

/** The rota's figure for a paper, summed across the rooms it is split into. */
export function officeCountFor(
  papers: { code: string; date: string; session: string; entries: number }[],
  code: string,
  date: string,
  session: string,
): number {
  return papers
    .filter((p) => p.code === code && p.date === date && p.session === session)
    .reduce((n, p) => n + (p.entries || 0), 0);
}

/* ── the day's work ───────────────────────────────────────────────── */

export interface RegisterRow {
  id: string;
  code: string;
  date: string;
  session: string;
  subject: string;
  start: string;
  end: string;
  entered: number;
  register: ExamRegister | null;
  state: RegisterState;
}

/** Registers still to be taken for papers that have already started — the
 *  queue the office chases. A paper that has not begun is not outstanding. */
export function outstanding(rows: RegisterRow[], startedBy: (row: RegisterRow) => boolean): RegisterRow[] {
  return rows.filter((row) => row.state === 'notTaken' && startedBy(row));
}
